/**
 * useTopicImages — the topic feature images, keyed by topic id.
 *
 * One cached query for the whole session: publication cards use it as the last
 * fallback when a publication has neither a featured image nor an author avatar.
 *
 * Each entry carries the topic the picture actually came from, so captions can
 * name the donor topic instead of guessing from the file URL (two topics may
 * legitimately share one picture file).
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { db } from '@/lib/supabaseQuery';
import { TOPIC_SEED_IMAGES } from '@/config/topicSeedImages';

export const TOPIC_IMAGES_QUERY_KEY = ['topics', 'featured-images'] as const;

export interface TopicImage {
  url: string;
  /** Topic the picture was taken from (the topic itself when curated). */
  topicId: string;
}

interface TopicImageRow {
  id: string;
  slug: string;
  featured_image_url: string | null;
}

export function useTopicImages(
  enabled = true,
  options: { fresh?: boolean } = {},
): UseQueryResult<Map<string, TopicImage>> {
  return useQuery({
    queryKey: TOPIC_IMAGES_QUERY_KEY,
    enabled,
    staleTime: 30 * 60 * 1000,
    // Editor screens always re-read so newly saved pictures show at once.
    refetchOnMount: options.fresh ? 'always' : false,
    queryFn: async (): Promise<Map<string, TopicImage>> => {
      const map = new Map<string, TopicImage>();

      // A topic's own saved picture wins; otherwise the unique illustration
      // shipped for that topic. Article pictures are never promoted here, so
      // two topics can never end up showing the same file.
      const rows = await db.fetchAll<TopicImageRow>('topics', 'id, slug, featured_image_url', {
        filters: [{ column: 'is_active', operator: 'eq', value: true }],
      });
      (rows ?? []).forEach(row => {
        const url = row.featured_image_url || TOPIC_SEED_IMAGES[row.slug];
        if (url) map.set(row.id, { url, topicId: row.id });
      });

      return map;
    },
  });
}

/**
 * Topic picture for a card: rotates through the topics that have one, so
 * neighbouring cards in a grid do not all repeat the same image. Returns the
 * picture together with the topic it came from.
 */
export function pickTopicImageEntry(
  images: Map<string, TopicImage> | undefined,
  topicIds: string[] | undefined,
  offset = 0,
): TopicImage | null {
  if (!images || !topicIds?.length) return null;
  const entries: TopicImage[] = [];
  for (const id of topicIds) {
    const entry = images.get(id);
    if (entry && !entries.some(e => e.url === entry.url)) entries.push(entry);
  }
  if (!entries.length) return null;
  return entries[Math.abs(offset) % entries.length];
}

/** Convenience wrapper for call sites that only need the URL. */
export function pickTopicImage(
  images: Map<string, TopicImage> | undefined,
  topicIds: string[] | undefined,
  offset = 0,
): string | null {
  return pickTopicImageEntry(images, topicIds, offset)?.url ?? null;
}
